import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockKey, ArrowLeft } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { t } = useI18n();

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError(t("reset.invalid_link"));
  }, [token, t]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate("/login"), 2200);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError(t("reset.invalid_link")); return; }
    if (pwd.length < 4) { setError(t("reset.too_short")); return; }
    if (pwd !== confirm) { setError(t("reset.mismatch")); return; }

    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`,
        { token, new_password: pwd }
      );
      setSuccess(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : t("generic.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-[#050505] flex flex-col">
      <PublicHeader langTestId="reset-lang" />
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <LockKey size={26} weight="fill" className="text-amber-500" />
            </div>
            <h1 className="text-2xl font-light tracking-tight text-white">{t("reset.title")}</h1>
          </div>

        {success ? (
          <div data-testid="reset-success" className="bg-[#121212] border border-emerald-500/20 rounded-lg p-6 text-center">
            <p className="text-sm text-emerald-400">{t("reset.success")}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#121212] border border-zinc-800 rounded-lg p-6 space-y-5">
            {error && (
              <div data-testid="reset-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded px-3 py-2">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("reset.new")}</Label>
              <Input
                data-testid="reset-new-input"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
                autoFocus
                className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 focus:ring-amber-500/20 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t("reset.confirm")}</Label>
              <Input
                data-testid="reset-confirm-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 focus:ring-amber-500/20 h-11"
              />
            </div>
            <Button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading || !token}
              className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 active:scale-[0.98] transition-all h-11"
            >
              {loading ? t("login.submitting") : t("reset.submit")}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="flex items-center justify-center gap-1 w-full text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <ArrowLeft size={14} /> {t("forgot.back_login")}
            </button>
          </form>
        )}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
